/**
 * Password strength validation according to Confluence OCC API Mapping Stage 1.
 */

// Allowed special characters: ! @ # $ % ^ * ( ) _ - + { } ; : . ,
export const ALLOWED_SPECIAL_CHARS = "!@#$%^*()_-+{};:.,";

// Matches ONLY lowercase, uppercase, digits, and the permitted special characters.
// Rejecting spaces, backslashes, quotes, ?, &, <, >, etc.
export const ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9!@#$%^*()_+\-{};:.,]+$/;

export const MIN_PASSWORD_LENGTH = 6;

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates a password against Stage 1 strength rules:
 * 1. Minimum 6 characters.
 * 2. At least one digit (0-9).
 * 3. At least one uppercase letter (A-Z).
 * 4. At least one special character from the allowed set (!@#$%^*()_+-{};:.,).
 * 5. Contains ONLY allowed characters (no spaces, no ?, &, <, >, /, \, ", ', ~, `, =).
 *
 * @param password The password string to validate.
 */
export function validatePasswordStrength(
  password: string
): PasswordValidationResult {
  const errors: string[] = [];

  if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(
      `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`
    );
  }

  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one digit (0-9).");
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter (A-Z).");
  }

  const hasSpecial = password
    .split("")
    .some((char) => ALLOWED_SPECIAL_CHARS.includes(char));
  if (!hasSpecial) {
    errors.push(
      "Password must contain at least one special character from the allowed set: " +
        ALLOWED_SPECIAL_CHARS.split("").join(" ")
    );
  }

  if (!ALLOWED_CHARS_REGEX.test(password)) {
    // Pinpoint when the trailing character is the only offender (the case
    // the backend flagged as confusing, since every other displayed rule
    // can look satisfied) so we can give a more specific message than the
    // generic "disallowed characters" one.
    const withoutLastChar = password.slice(0, -1);
    const onlyLastCharInvalid =
      withoutLastChar.length > 0 && ALLOWED_CHARS_REGEX.test(withoutLastChar);

    if (onlyLastCharInvalid) {
      errors.push(
        "Password cannot end with that character. The last character must be a letter, a digit, or one of: " +
          ALLOWED_SPECIAL_CHARS.split("").join(" ")
      );
    } else {
      errors.push(
        "Password contains disallowed characters. Only letters, digits, and basic special characters (! @ # $ % ^ * ( ) _ - + { } ; : . ,) are allowed, with no spaces."
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
